<template>
  <UIPopup
    v-model="modelValue"
    :title="$t('properties.filters.title')"
    :button-text="$t('properties.filters.apply')"
    :close-text="$t('properties.filters.reset')"
    size="extraBig"
    :secondary-button-closes="false"
    max-width="1380px"
    closeable
    @close="emit('update:modelValue', false)"
  >
    <template #header>
      <div class="text-headline-4 flex justify-start">{{ $t('properties.filters.title') }}</div>
    </template>
    <template #body>
      <div class="grid grid-cols-1 gap-3 overflow-hidden lg:grid-cols-2 lg:gap-12 xl:grid-cols-3">
        <div class="flex flex-col gap-8">
          <UIDropdown
            id="emirate"
            v-model="localFilters.emirate_names"
            :items="emirateNames"
            name="emirateNames"
            :loading="isLoadingEmirateNames"
            multiple
            compact
            sync-v-model
            :menu-height="MENU_HEIGHT"
            :controlled="false"
            :label="$t('properties.filters.emirate')"
            :placeholder="$t('properties.filters.emirate')"
            persistent-placeholder
          />
          <UIDropdown
            id="location_ids"
            v-model="localFilters.location_ids"
            :items="locations"
            name="districts"
            :loading="isLoadingLocations"
            multiple
            compact
            sync-v-model
            :menu-height="MENU_HEIGHT"
            :controlled="false"
            :label="$t('properties.filters.cityDistricts')"
            :placeholder="$t('properties.filters.cityDistricts')"
            persistent-placeholder
          />
          <UIDropdown
            id="apartment-types"
            v-model="localFilters.apartment_type"
            :items="apartmentTypes"
            :loading="isLoadingApartmentTypes"
            name="apartmentTypes"
            label-key="name"
            value-key="id"
            multiple
            compact
            sync-v-model
            :menu-height="MENU_HEIGHT"
            :controlled="false"
            :label="$t('properties.filters.propertyType')"
            :placeholder="$t('properties.filters.propertyType')"
            persistent-placeholder
          />

          <div class="flex flex-row items-end gap-4">
            <UIInputText
              id="area-from"
              v-model="localFilters.area_min"
              name="area-from"
              :label="$t('properties.filters.propertyArea')"
              :placeholder="$t('properties.filters.from')"
              suffix="m²"
              sync-v-model
              :controlled="false"
              compact
              :data-maska="'#'.repeat(9)"
            />

            <span class="mb-3">-</span>
            <UIInputText
              id="area-to"
              v-model="localFilters.area_max"
              name="area-to"
              :placeholder="$t('properties.filters.to')"
              suffix="m²"
              sync-v-model
              :controlled="false"
              compact
              :data-maska="'#'.repeat(9)"
            />
          </div>
          <UIDropdown
            v-model="localFilters.amenities"
            :items="amenities"
            :loading="isLoadingAmenities"
            multiple
            compact
            :label="$t('properties.filters.amenities')"
            name="Amenities"
            sync-v-model
            :menu-height="MENU_HEIGHT"
            :controlled="false"
            :placeholder="$t('properties.filters.amenities')"
            persistent-placeholder
          />
        </div>
        <div class="flex flex-col gap-8">
          <UIInputOptions
            id="stage"
            v-model="localFilters.building_status"
            name="stage"
            :items="buildingStatusesFormatted"
            label-key="name"
            value-key="id"
            :loading="isLoadingBuildingStatuses"
            :label="$t('properties.filters.constructionStages')"
          />
          <UIInputOptions
            id="bedrooms"
            v-model="localFilters.bedrooms"
            name="bedrooms"
            :items="BEDROOMS_ITEMS"
            :label="$t('properties.filters.bedrooms')"
            multiple
          />
          <div class="flex flex-row items-center gap-4">
            <UIInputText
              id="budget"
              v-model="localFilters.price_max"
              name="budget"
              :label="$t('properties.filters.budget')"
              :data-maska="'#'.repeat(9)"
              :prefix="$t('properties.filters.upToAED')"
              input-class="pl-[95px]"
              sync-v-model
              :controlled="false"
              compact
            />
            <UIInputText
              id="down-payment"
              v-model="localFilters.down_payment"
              name="down-payment"
              :label="$t('properties.filters.downPayment')"
              :data-maska="'#'.repeat(9)"
              :prefix="$t('properties.filters.upToAED')"
              input-class="pl-[95px]"
              sync-v-model
              :controlled="false"
              compact
            />
          </div>
          <UIDropdown
            v-model="localFilters.sale_status"
            :items="salesStatuses"
            :loading="isLoadingSalesStatuses"
            multiple
            compact
            :label="$t('properties.filters.salesStatus')"
            name="sales-status"
            sync-v-model
            :menu-height="MENU_HEIGHT"
            :controlled="false"
            :placeholder="$t('properties.filters.any')"
            persistent-placeholder
          />
        </div>
        <div class="flex flex-col gap-8">
          <div class="flex flex-col gap-2">
            <div class="flex flex-row items-center gap-2">
              <UIInputText
                id="floor-from"
                name="floor-from"
                :placeholder="$t('properties.filters.floor.notLower')"
                :label="$t('properties.filters.floor.label')"
                disabled
                model-value=""
                compact
              />
              <UIInputText
                id="floor-to"
                name="floor-to"
                :placeholder="$t('properties.filters.floor.notHigher')"
                :label="$t('properties.filters.floor.label')"
                disabled
                model-value=""
                compact
              />
            </div>
            <UICheckbox v-model="localFilters.floor_plan" name="floorPlanAvailable" sync-v-model :controlled="false">
              {{ $t('properties.filters.floor.planAvailable') }}
            </UICheckbox>
          </div>
          <!-- <UICheckbox
            v-model="localFilters.post_handover_payment_plan"
            sync-v-model
            :controlled="false"
            name="postHandoverPaymentPlan"
            label=""
          >
            Post-handover payment plan
          </UICheckbox>
          <UICheckbox v-model="localFilters.roi_percent" sync-v-model :controlled="false" name="guaranteedROI">
            Guaranteed ROI
          </UICheckbox> -->
        </div>
      </div>
    </template>
    <template #buttons>
      <slot name="buttons">
        <UIButton id="popup-close" compact variant="secondary" @click="reset">
          {{ $t('properties.filters.reset') }}
        </UIButton>
        <UIButton id="popup-apply" compact class="popup__content-buttons-apply" @click="applyFilters">
          {{ $t('properties.filters.apply') }}
        </UIButton>
      </slot>
    </template>
  </UIPopup>
</template>

<script setup lang="ts">
import cloneDeep from 'lodash/cloneDeep'
import { BEDROOMS_ITEMS } from '~/constants/properties'
import type { Filters } from '~/types/properties'

const emit = defineEmits(['update:modelValue', 'cancel', 'input', 'reset'])

const modelValue = defineModel<boolean>({ required: true })

const MENU_HEIGHT = '270px'

type Props = {
  filters: Filters
}

const props = defineProps<Props>()

const localFilters = ref<Filters>({ ...cloneDeep(props.filters) })

const { data: apartmentTypes, isLoading: isLoadingApartmentTypes } = usePCDictionary(
  PROPERTIES_DICTIONARY_KEYS.APARTMENT_TYPE
)
const { data: locations, isLoading: isLoadingLocations } = usePCDictionary(PROPERTIES_DICTIONARY_KEYS.LOCATIONS)
const { data: amenities, isLoading: isLoadingAmenities } = usePCDictionary(PROPERTIES_DICTIONARY_KEYS.AMENITIES)
const { data: emirateNames, isLoading: isLoadingEmirateNames } = usePCDictionary(
  PROPERTIES_DICTIONARY_KEYS.EMIRATE_NAMES
)

const { data: buildingStatuses, isLoading: isLoadingBuildingStatuses } = usePCDictionary(
  PROPERTIES_DICTIONARY_KEYS.BUILDING_STATUSES
)

const buildingStatusesFormatted = computed(() => {
  return [
    {
      id: null,
      name: 'Any',
    },
    ...(buildingStatuses.value || []),
  ]
})

const { data: salesStatuses, isLoading: isLoadingSalesStatuses } = usePCDictionary(
  PROPERTIES_DICTIONARY_KEYS.SALE_STATUSES
)

const reset = () => {
  localFilters.value = {
    query: '',
    property_type_ids: [],
    sale_status: [],
    bedrooms: [],
    budget: [],
    order_by: '',
    order_way: '',
  }

  emit('reset')
}

const applyFilters = () => {
  emit('input', localFilters.value)

  modelValue.value = false
}
</script>

<style scoped></style>
